import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveOrg } from "common-fe";
import useFoxboxInstances from "../hooks/useFoxboxInstances";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

// #2373 — superuser-only foxbox instance config, reachable from the left nav
// (App.jsx's "Superuser/Foxbox" item, isSuper-gated) and NOT show-scoped: it
// never reads useActiveShow, so it renders identically no matter which show
// is active or whether one is selected at all (card AC2). Consumes card
// #2372's routes via useFoxboxInstances (score-common's generated InstanceApi
// client, never a direct fetch).
//
// Two distinct "enabled" flags per card text: the per-show switch and the
// instance-level kill switch. The instance flag overrides every show under
// it, so a show reading ON while its instance is OFF is not syncing — the
// per-instance banner below (instanceOffWarning) says so explicitly rather
// than letting the per-show switches imply otherwise.
//
// Card #2418 — the "does this file actually open on the box" green/red
// indicator, filling the "File health" column the comment above used to
// note was deliberately left for a sibling card. #2417 (score-api) stores
// and serves fileHealth per show, keyed sdf/hr: { status, message,
// checkedAt }. SIX statuses, not five — see FILE_HEALTH_STATUSES below;
// `unknown` is the default #2417 fills in server-side for a show that has
// never reported, so there is no `undefined` to guard against here.
//
// Wire-shape gap (#2417 judge finding): on write, `hr: null` (no hrPath
// configured) normalizes to `hr: { status: "unknown" }` on read, same as a
// configured hrPath that simply hasn't reported yet. fileHealth ALONE
// cannot tell those two cases apart. This page can, because it already has
// each show's hrPath from the same config payload — see the `hasPath`
// branch in FileHealthCell below, which renders not-applicable (AC6)
// whenever hrPath is falsy and only ever consults fileHealth.hr when an
// hrPath is actually configured.
export const FILE_HEALTH_STATUSES = Object.freeze([
  "ok",
  "not_found",
  "no_permission",
  "locked",
  "invalid",
  "unknown",
]);

// A box calls home on its own configPollMs cadence (instance.intervals,
// #2417/#1191 — each instance carries its own, default 60000ms). One missed
// poll can just be an ordinary slow tick; several missed polls in a row
// means the box has plainly stopped calling home. STALE_POLL_MULTIPLIER
// turns that per-instance interval into a staleness threshold instead of a
// hard-coded absolute ms value that would drift out of sync with whatever
// cadence a given box is actually configured for. 3 is picked so one or two
// slow/late polls (network hiccup, a box briefly busy on its write pull)
// never falsely read as dead, while a box that's actually stopped reporting
// reliably crosses it within a few minutes on the default cadence.
export const STALE_POLL_MULTIPLIER = 3;

// AC5 — a result whose checkedAt is old enough that the box has plainly
// stopped calling home renders as unknown, not as whatever it last
// reported. This overrides ANY last-reported status, not just "ok": a
// stale "locked" is just as untrustworthy as a stale "ok" once the box
// hasn't confirmed anything recently — the reader has no way to know the
// file is still locked and not, say, deleted, restored, or the whole box
// powered off.
function isStale(checkedAt, configPollMs, now) {
  if (!checkedAt) return false; // never-reported already reads "unknown" from the server
  const checkedMs = Date.parse(checkedAt);
  if (Number.isNaN(checkedMs)) return false;
  const threshold = (configPollMs || DEFAULT_CONFIG_POLL_MS) * STALE_POLL_MULTIPLIER;
  return now - checkedMs > threshold;
}

// Matches score-api's DEFAULT_INSTANCE_INTERVALS.configPollMs
// (apps/score-api/src/handlers/instance.js) — only used as a fallback if a
// row's intervals were ever missing configPollMs, which the API's own
// normalization already prevents.
const DEFAULT_CONFIG_POLL_MS = 60000;

function effectiveFileHealthStatus(entry, configPollMs, now) {
  if (!entry) return "unknown";
  // Distrust an entry with no usable checked time exactly like a
  // never-reported one, rather than falling through to isStale() (which
  // itself already reads "no checkedAt" as "not stale") and then trusting
  // whatever status came with it. Today score-api's own normalization never
  // produces this shape (checkedAt is always stamped, status always
  // clamped to FILE_HEALTH_STATUSES), so no live path reaches this branch
  // -- but this page's whole job is distrusting a stale/malformed upstream,
  // so it should not depend on that guarantee holding on the other side.
  if (!entry.checkedAt || Number.isNaN(Date.parse(entry.checkedAt))) return "unknown";
  if (isStale(entry.checkedAt, configPollMs, now)) return "unknown";
  return FILE_HEALTH_STATUSES.includes(entry.status) ? entry.status : "unknown";
}

// AC2 — every non-ok, non-unknown status gets its OWN label, not one
// undifferentiated red. Switch on a literal status string per branch (never
// a built key like `foxbox.fileHealth.status.${status}`) so every t() call
// site stays a static literal for the i18n extraction tooling.
function fileHealthStatusLabel(t, status) {
  switch (status) {
    case "ok":
      return t("foxbox.fileHealth.status.ok");
    case "not_found":
      return t("foxbox.fileHealth.status.notFound");
    case "no_permission":
      return t("foxbox.fileHealth.status.noPermission");
    case "locked":
      return t("foxbox.fileHealth.status.locked");
    case "invalid":
      return t("foxbox.fileHealth.status.invalid");
    case "unknown":
    default:
      return t("foxbox.fileHealth.status.unknown");
  }
}

function fileHealthChipColor(status) {
  if (status === "ok") return "success";
  if (status === "unknown") return "warning";
  return "error"; // not_found / no_permission / locked / invalid
}

// Same display convention as common-fe's RunHistory.jsx: fall back to the
// raw ISO string if it somehow fails to parse, rather than showing nothing.
function formatCheckedAt(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// One file's health indicator (sdf or hr) for one show. `hasPath` is driven
// by the show's own sdfPath/hrPath, never by fileHealth alone — see the
// file-header comment on the wire-shape gap this resolves (AC6).
function FileHealthCell({ fileLabel, hasPath, entry, configPollMs, t, testId, now }) {
  if (!hasPath) {
    return (
      <Stack spacing={0.25} data-testid={testId}>
        <Chip
          label={`${fileLabel}: ${t("foxbox.fileHealth.notApplicable")}`}
          size="small"
          variant="outlined"
          data-testid={`${testId}-chip`}
        />
      </Stack>
    );
  }

  const safeEntry = entry || { status: "unknown", message: null, checkedAt: null };
  const status = effectiveFileHealthStatus(safeEntry, configPollMs, now);
  const statusLabel = fileHealthStatusLabel(t, status);
  const showReason = status !== "ok" && status !== "unknown" && safeEntry.message;
  const chipLabel = showReason ? `${fileLabel}: ${statusLabel} — ${safeEntry.message}` : `${fileLabel}: ${statusLabel}`;
  const checkedLabel = safeEntry.checkedAt
    ? t("foxbox.fileHealth.lastChecked", { time: formatCheckedAt(safeEntry.checkedAt) })
    : t("foxbox.fileHealth.neverReported");

  return (
    <Stack spacing={0.25} data-testid={testId}>
      <Chip label={chipLabel} color={fileHealthChipColor(status)} size="small" data-testid={`${testId}-chip`} />
      <Typography variant="caption" color="text.secondary" data-testid={`${testId}-checked`}>
        {checkedLabel}
      </Typography>
    </Stack>
  );
}

function draftFromInstance(instance) {
  return {
    enabled: instance.enabled === true,
    shows: (instance.shows || []).map((s) => ({ ...s })),
  };
}

function isDirty(draft, instance) {
  if (!draft) return false;
  return JSON.stringify(draft) !== JSON.stringify(draftFromInstance(instance));
}

function InstanceRow({ instance, t, saveInstance }) {
  const [draft, setDraft] = useState(() => draftFromInstance(instance));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-sync the local draft whenever the underlying instance row changes —
  // on initial load, on a manual refetch, and after THIS row's own save
  // resolves (useFoxboxInstances.saveInstance replaces this instance with
  // the server's post-write document). Never resets while the user is still
  // mid-edit on an unrelated instance, since each row owns its own draft.
  useEffect(() => {
    setDraft(draftFromInstance(instance));
    setError(null);
  }, [instance]);

  // File-health staleness is evaluated against THIS instance's own
  // configPollMs (#2417 — each box's cadence is per-instance, not global),
  // and against "now" at render time — no polling loop here, "no polish"
  // per card text; a manual refetch/reload re-evaluates it same as any
  // other field.
  const configPollMs = (instance.intervals && instance.intervals.configPollMs) || DEFAULT_CONFIG_POLL_MS;
  const now = Date.now();

  const dirty = isDirty(draft, instance);
  const anyShowOnWhileInstanceOff = !draft.enabled && draft.shows.some((s) => s.enabled);

  const updateShow = (showId, field, value) => {
    setDraft((d) => ({
      ...d,
      shows: d.shows.map((s) => (s.showId === showId ? { ...s, [field]: value } : s)),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // hrPath: an emptied field is sent as null, not "". "" is still typeof
      // "string" server-side (instance.js's validateShowsPaths) and would
      // fail the ".hdf" extension check; null is nullable and skips it.
      const shows = draft.shows.map((s) => ({ ...s, hrPath: s.hrPath ? s.hrPath : null }));
      await saveInstance(instance.instanceId, { enabled: draft.enabled, shows });
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }} data-testid="foxbox-instance-row">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="h6" color="text.primary" data-testid="foxbox-instance-id">
            {instance.instanceId}
          </Typography>
          <Chip
            label={draft.enabled ? t("foxbox.instanceOn") : t("foxbox.instanceOff")}
            color={draft.enabled ? "success" : "error"}
            size="small"
            data-testid="foxbox-instance-enabled-chip"
          />
        </Stack>
        <FormControlLabel
          control={
            <Switch
              checked={draft.enabled}
              onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
              data-testid="foxbox-instance-enabled-switch"
              inputProps={{ "aria-label": t("foxbox.instanceEnabledSwitchLabel") }}
            />
          }
          label={t("foxbox.instanceEnabledSwitchLabel")}
        />
      </Stack>

      {anyShowOnWhileInstanceOff && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="foxbox-instance-off-warning">
          {t("foxbox.instanceOffWarning")}
        </Alert>
      )}

      {draft.shows.length === 0 ? (
        <Typography color="text.secondary">{t("foxbox.noShows")}</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("foxbox.columns.showId")}</TableCell>
                <TableCell>{t("foxbox.columns.orgId")}</TableCell>
                <TableCell>{t("foxbox.columns.sdfPath")}</TableCell>
                <TableCell>{t("foxbox.columns.hrPath")}</TableCell>
                <TableCell>{t("foxbox.columns.fileHealth")}</TableCell>
                <TableCell align="center">{t("foxbox.columns.enabled")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {draft.shows.map((show) => (
                <TableRow key={show.showId} hover data-testid="foxbox-show-row">
                  <TableCell data-testid="foxbox-show-id">{show.showId}</TableCell>
                  <TableCell data-testid="foxbox-show-org-id">{show.orgId}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={show.sdfPath || ""}
                      onChange={(e) => updateShow(show.showId, "sdfPath", e.target.value)}
                      data-testid="foxbox-show-sdf-path"
                      inputProps={{ "aria-label": t("foxbox.columns.sdfPath") }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={show.hrPath || ""}
                      onChange={(e) => updateShow(show.showId, "hrPath", e.target.value)}
                      data-testid="foxbox-show-hr-path"
                      inputProps={{ "aria-label": t("foxbox.columns.hrPath") }}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      <FileHealthCell
                        fileLabel={t("foxbox.fileHealth.sdfLabel")}
                        hasPath={Boolean(show.sdfPath)}
                        entry={show.fileHealth && show.fileHealth.sdf}
                        configPollMs={configPollMs}
                        t={t}
                        now={now}
                        testId="foxbox-show-sdf-health"
                      />
                      <FileHealthCell
                        fileLabel={t("foxbox.fileHealth.hrLabel")}
                        hasPath={Boolean(show.hrPath)}
                        entry={show.fileHealth && show.fileHealth.hr}
                        configPollMs={configPollMs}
                        t={t}
                        now={now}
                        testId="foxbox-show-hr-health"
                      />
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={show.enabled === true}
                      onChange={(e) => updateShow(show.showId, "enabled", e.target.checked)}
                      data-testid="foxbox-show-enabled-switch"
                      inputProps={{ "aria-label": t("foxbox.columns.enabled") }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="foxbox-save-error">
          {t("foxbox.saveError")}
        </Alert>
      )}

      <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          disabled={!dirty || saving}
          onClick={handleSave}
          data-testid="foxbox-save-button"
        >
          {saving ? t("foxbox.savingButton") : t("foxbox.saveButton")}
        </Button>
        {dirty && !saving && (
          <Typography variant="body2" color="text.secondary" data-testid="foxbox-unsaved-indicator">
            {t("foxbox.unsavedChanges")}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

export default function FoxboxInstances() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.foxbox"));
  const { isSuper } = useActiveOrg();
  const { instances, loading, error, saveInstance } = useFoxboxInstances();

  // AC5 — this page is superuser-only, a stricter bar than ScoreEntitlementGate
  // (which lets any score-entitled, score-admin-role org member into the
  // whole admin shell). The nav item is already hidden for a non-super user
  // (App.jsx), but that alone doesn't stop a typed URL — this redirect is the
  // actual enforcement for that case.
  if (!isSuper) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("foxbox.title")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t("foxbox.subtitle")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("foxbox.loadError")}
        </Alert>
      )}

      {loading ? (
        <TableSkeleton rows={4} columns={5} />
      ) : instances.length === 0 ? (
        <Typography color="text.secondary">{t("foxbox.empty")}</Typography>
      ) : (
        instances.map((instance) => (
          <InstanceRow
            key={instance.instanceId}
            instance={instance}
            t={t}
            saveInstance={saveInstance}
          />
        ))
      )}
    </Box>
  );
}
